import React, { useState, useEffect, useMemo } from "react";
import { Box, Fab, Button } from "@mui/material";
import LocalizedInput from "../LocalizedInput";
import { emptyLocalizedFields } from "../../../config/localization";
import TextInput from "../TextInput";
import isEqual from "lodash.isequal";
import VirtualizedAutoCompleteField from "../VirtualizedAutoCompleteField";
import { useData } from "../../../contexts/DataContext/DataContextt";
import { usePreparationData } from "../../../contexts/hooks/usePreparationData";
import { useSearch } from "../../../contexts/hooks/useReusableHook";
import { fetchGroups } from "../../../services/groupService";
import { useNavigate } from "react-router-dom";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";

export const ClassForm = ({ props, onSave }) => {
  const { language } = useData();
  const navigate = useNavigate();
  const { confirmDialog } = usePreload();
  const defaultClassValue = useMemo(
    () => ({
      classCode: props.row.classCode ?? "",
      /*className: {
        ...emptyLocalizedFields,
        [language]: props.row.className ?? "",
      },
      classDisplayName: {
        ...emptyLocalizedFields,
        [language]: props.row.classDisplayName ?? "",
      },
      classDescription: {
        ...emptyLocalizedFields,
        [language]: props.row.classDescription ?? "",
      },*/
      className: props.row.className || emptyLocalizedFields,
      classDisplayName: props.row.classDisplayName || emptyLocalizedFields,
      classDescription: props.row.classDescription || emptyLocalizedFields,
      classGroupCode: props.row.classGroupCode ?? "",
    }),
    [props.row, language],
  );

  const { groupOptions } = usePreparationData({ sources: ["groups"] });
  const {
    options: groupLocalOptions,
    search: searchGroups,
    searching: isGroupSearching,
  } = useSearch(groupOptions, fetchGroups, {
    displayNameFields: ["groupDisplayName", "groupName"],
    extraKeys: [],
  });

  const [classValue, setClassValue] = useState(defaultClassValue);
  const [classValueOriginal, setClassValueOriginal] =
    useState(defaultClassValue);

  const cancelShow = props?.showCancel ?? true;
  const saveText = props?.saveText ?? "Save";

  const handleSave = async () => {
    onSave(classValue);

    if (!props.isUpdate) {
      setClassValue({ ...defaultClassValue });
      setClassValueOriginal({ ...defaultClassValue });
    } else setClassValueOriginal(classValue);
  };

  useEffect(() => {
    setClassValue(defaultClassValue);
    setClassValueOriginal(defaultClassValue);
  }, [defaultClassValue]);

  const hasChanges = !isEqual(classValue, classValueOriginal);

  return (
    <Box>
      <TextInput
        label="Class Code *"
        disabled={props.isUpdate}
        value={classValue.classCode}
        onChange={(e) => {
          setClassValue((prev) => ({
            ...prev,
            classCode: e.target.value,
          }));
        }}
      />
      <Box mt={2}>
        <VirtualizedAutoCompleteField
          options={groupLocalOptions}
          limit={1}
          label="Group Code"
          value={classValue.classGroupCode?.split("|") ?? []}
          onChange={(val) => {
            setClassValue((prev) => ({
              ...prev,
              classGroupCode: (Array.isArray(val) ? val : [val]).join("|"),
            }));
          }}
          searching={isGroupSearching}
          onSearch={searchGroups}
        />
      </Box>

      <LocalizedInput
        label="Class Name"
        value={classValue.className}
        onChange={(val) => {
          setClassValue((prev) => ({ ...prev, className: val }));
        }}
        isUpdate={props.isUpdate}
      />
      <LocalizedInput
        label="Class Displayname"
        value={classValue.classDisplayName}
        onChange={(val) => {
          setClassValue((prev) => ({ ...prev, classDisplayName: val }));
        }}
        multiline
        rows={2}
        isUpdate={props.isUpdate}
      />
      <LocalizedInput
        label="Class Description"
        value={classValue.classDescription}
        onChange={(val) => {
          setClassValue((prev) => ({ ...prev, classDescription: val }));
        }}
        multiline
        rows={2}
        isUpdate={props.isUpdate}
      />
      <Box mt={2} display="flex" justifyContent="right" gap={2}>
        <Button
          onClick={async () => {
            if (hasChanges) {
              const ok = await confirmDialog.show({
                title: "Unsaved Changes",
                message: `Unsaved changes detected, Would you like to cancel?`,
              });
              if (!ok) {
                return;
              }
            }
            confirmDialog.close();
            if (props?.isUpdate && props?.row?.classCode) {
              navigate(`/classes/${props?.row?.classCode}/includedfeatures`, {
                state: { row: props?.row },
              });
            } else navigate("/classNew");
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!hasChanges}
          loading={props.uploading}
          onClick={handleSave}
        >
          {saveText}
        </Button>
      </Box>
    </Box>
  );
};
